<template>
  <div>
    <BusinessEntityInput
      v-model="companyName"
      :error="errorCompanyName"
      :label="$t('Business name / Trade name')"
      name="registered_company_name"
      @focusout="v$.companyName.$touch"
    />

    <div class="tw-mt-4">
      <CompanyNumberInput market="NL">
        <BusinessEntityInput
          v-model="companyNo"
          :error="errorCompanyNo"
          :label="''"
          placeholder="12345678"
          name="company_no"
          @focusout="v$.companyNo.$touch"
        />
      </CompanyNumberInput>
      <WdsTag v-if="kvkValidationStatus === 'valid'" success class="tw-mt-1">
        {{ $t('KvK valid') }}
      </WdsTag>
      <WdsTag v-else-if="kvkValidationStatus === 'invalid'" error class="tw-mt-1">
        {{ $t('KvK invalid') }}
      </WdsTag>
    </div>

    <FeatureFlagged name="vat-rate-selection-netherlands">
      <div class="tw-mt-5">
        <BusinessEntityIsVatRegistered v-model="isVatRegistered" market="NL" />
      </div>

      <div
        v-if="isVatRegistered"
        class="tw-m-4 tw-p-4 tw-border-solid tw-border tw-rounded tw-border-gray-600"
      >
        <div class="tw-mb-4">
          <NlBusinessEntityTaxRegistrationTypeRadioGroup
            v-model="vatStatus"
            name="tax_registration_type"
          />
        </div>

        <BusinessEntityInput
          v-if="['nl_standard', 'nl_reduced'].includes(vatStatus)"
          v-model="vatNumber"
          :label="$t('BTW number (VAT identification)')"
          name="vat_no"
          placeholder="NL123456789B01"
          required
        />

        <WdsDivider class="tw-my-4" />

        <WdsText secondary>
          {{
            $t(
              'This VAT rate will be applied to all your invoices. If a specific contract requires a different rate, please update your profile or contact support.',
            )
          }}
        </WdsText>
      </div>

      <template #is-disabled>
        <BusinessEntityVat
          v-model:business-entity="internalBusinessEntity"
          :compliance-data="complianceData"
          market="NL"
          class="tw-mt-5"
          @update:is-vat-registered="$emit('update:isVatRegistered', $event)"
        />
      </template>
    </FeatureFlagged>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { computed, ref } from 'vue'
import { WdsDivider, WdsTag, WdsText } from '@worksome/wds-components'
import BusinessEntityInput from '@/components/compliances/BusinessEntity/BusinessEntityInput.vue'
import BusinessEntityVat from '@/components/compliances/BusinessEntity/BusinessEntityVat.vue'
import CompanyNumberInput from '@/components/compliances/BusinessEntity/CompanyNumberInput.vue'
import {
  commonCompanyName,
  commonCompanyNumber,
} from '@/components/compliances/BusinessEntity/Validations/BusinessEntityValidationRules'
import { useValidatorWithGraphQlError } from '@/composables/errors'
import BusinessEntityIsVatRegistered from '@/components/compliances/BusinessEntity/BusinessEntityIsVatRegistered.vue'
import { setdefault } from '@/utils/tools'
import { $t } from '@/composables/translation'
import NlBusinessEntityTaxRegistrationTypeRadioGroup from '@/components/compliances/BusinessEntity/Nl/NlBusinessEntityTaxRegistrationTypeRadioGroup.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

export default {
  components: {
    FeatureFlagged,
    NlBusinessEntityTaxRegistrationTypeRadioGroup,
    WdsDivider,
    WdsText,
    BusinessEntityIsVatRegistered,
    BusinessEntityInput,
    BusinessEntityVat,
    CompanyNumberInput,
    WdsTag,
  },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    errorsGraphql: {
      type: Object,
      default() {
        return null
      },
    },
  },
  // `update:isVatRegistered` is forwarded from BusinessEntityVat so the
  // page-level parent can track the user's explicit toggle state.
  emits: ['update:businessEntity', 'update:isVatRegistered'],
  setup(props, { emit }) {
    const v$ = useVuelidate()
    const errors = ref(props.errorsGraphql)

    const internalBusinessEntity = ref(props.businessEntity)

    const companyName = computed({
      get() {
        return internalBusinessEntity.value.companyName
      },

      set(value) {
        internalBusinessEntity.value.companyName = value
        v$.value.companyName.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyName = useValidatorWithGraphQlError(
      v$,
      errors,
      'companyName',
      'registered_company_name',
    )

    const companyNo = computed({
      get() {
        return internalBusinessEntity.value.companyNo
      },
      set(value) {
        internalBusinessEntity.value.companyNo = value
        v$.value.companyNo.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyNo = useValidatorWithGraphQlError(v$, errors, 'companyNo', 'company_no')

    const kvkValidationStatus = computed(
      () => internalBusinessEntity.value.data?.kvk_validation_status ?? null,
    )

    const isVatRegistered = computed({
      get() {
        return internalBusinessEntity.value.data?.is_vat_registered
      },
      set(value) {
        const data = setdefault(internalBusinessEntity.value, 'data', {})
        data.is_vat_registered = value
        emit('update:businessEntity', internalBusinessEntity.value)
        emit('update:isVatRegistered', value)
      },
    })

    const vatStatus = computed({
      get() {
        return internalBusinessEntity.value.data?.tax_registration_type ?? 'nl_standard'
      },
      set(value) {
        const data = setdefault(internalBusinessEntity.value, 'data', {})
        data.tax_registration_type = value
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })

    const vatNumber = computed({
      get() {
        return internalBusinessEntity.value.taxNo
      },
      set(value) {
        internalBusinessEntity.value.taxNo = value
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })

    return {
      companyName,
      companyNo,
      errorCompanyName,
      errorCompanyNo,
      kvkValidationStatus,
      internalBusinessEntity,
      isVatRegistered,
      vatNumber,
      vatStatus,
      v$,
    }
  },
  methods: { $t },

  validations() {
    return {
      companyName: commonCompanyName(),
      companyNo: commonCompanyNumber(),
    }
  },
}
</script>
